import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, CheckCircle2, RefreshCw, ExternalLink, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import AdminLayout from '../components/AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalendarSettings = () => {
  const [status, setStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const { data } = await axios.get(`${API}/admin/calendar/status`);
      setStatus(data);
      if (data.connected && !data.serviceAccount) loadCalendars();
    } catch (e) {
      toast.error('Failed to load calendar status');
    } finally {
      setLoadingStatus(false);
    }
  };

  const loadCalendars = async () => {
    setLoadingCalendars(true);
    try {
      const { data } = await axios.get(`${API}/admin/calendar/list`);
      setCalendars(data.calendars || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch calendars');
    } finally {
      setLoadingCalendars(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = () => {
    window.location.href = `${API}/oauth/calendar/authorize`;
  };

  const handleSelect = async (cal) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/calendar/select`, {
        calendarId: cal.id,
        calendarName: cal.summary,
      });
      toast.success(`Selected: ${cal.summary}`);
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save selection');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? New bookings will no longer create events until reconnected.')) return;
    try {
      await axios.post(`${API}/admin/calendar/disconnect`);
      toast.success('Google Calendar disconnected');
      setStatus(null);
      setCalendars([]);
      await loadStatus();
    } catch (e) {
      toast.error('Failed to disconnect');
    }
  };

  return (
    <AdminLayout
      title="Calendar Settings"
      subtitle="Auto-add confirmed bookings to your Google Calendar"
    >
      {loadingStatus ? (
        <p className="text-gray-600">Loading…</p>
      ) : status?.serviceAccount ? (
        <Card className={`p-8 max-w-2xl border-l-4 ${status.connected ? 'border-l-green-600' : 'border-l-amber-500'}`} data-testid="calendar-service-account-card">
          <div className="flex items-start gap-4">
            <CalendarIcon className={`h-10 w-10 shrink-0 ${status.connected ? 'text-green-600' : 'text-amber-500'}`} />
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-black mb-1" data-testid="calendar-sa-status">
                {status.connected ? 'Permanently connected' : 'Waiting for calendar access'}
              </h2>
              <p className="text-gray-600 text-sm mb-3">
                {status.connected
                  ? 'Confirmed bookings sync automatically through a service account. It never needs reconnecting.'
                  : `${status.serviceAccountError}. Share the booking calendar with the email below and choose "Make changes to events".`}
              </p>
              <p className="text-xs text-gray-500">Service account</p>
              <p className="font-mono text-sm text-black break-all mb-2" data-testid="calendar-sa-email">{status.connectedEmail}</p>
              <p className="text-xs text-gray-500">Calendar ID</p>
              <p className="font-mono text-xs text-black break-all" data-testid="calendar-sa-calendar-id">{status.selectedCalendarId}</p>
              <Button onClick={loadStatus} variant="outline" className="mt-4" data-testid="calendar-sa-recheck-btn">Check again</Button>
            </div>
          </div>
        </Card>
      ) : status?.needsReconnect ? (
        <Card className="p-10 text-center max-w-2xl border-l-4 border-l-amber-500" data-testid="calendar-revoked-card">
          <CalendarIcon className="h-12 w-12 mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-bold text-black mb-2">Reconnect Google Calendar</h2>
          <p className="text-gray-600 mb-2 max-w-md mx-auto">
            Your previous connection ({status.connectedEmail}) has been revoked by Google
            (token expired or access was removed). Confirmed bookings stopped syncing.
          </p>
          <p className="text-amber-700 mb-6 max-w-md mx-auto text-sm">
            Click below to reconnect — new confirmed bookings will resume calendar sync immediately.
          </p>
          <Button
            onClick={handleConnect}
            className="bg-amber-500 text-white hover:bg-amber-600"
            data-testid="reconnect-calendar-btn"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Reconnect with Google
          </Button>
        </Card>
      ) : !status?.connected ? (
        <Card className="p-10 text-center max-w-2xl" data-testid="calendar-disconnected-card">
          <CalendarIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-bold text-black mb-2">Connect Google Calendar</h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            When a customer accepts a quote, the booking will be added straight to the calendar you choose.
          </p>
          <Button
            onClick={handleConnect}
            className="bg-black text-white hover:bg-gray-800"
            data-testid="connect-calendar-btn"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Connect with Google
          </Button>
        </Card>
      ) : (
        <div className="space-y-6 max-w-3xl">
          <Card className="p-6" data-testid="calendar-connected-card">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-black">Connected</p>
                  <p className="text-sm text-gray-600">{status.connectedEmail}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={loadCalendars}
                  variant="outline"
                  className="border-gray-300"
                  data-testid="refresh-calendars-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh List
                </Button>
                <Button
                  onClick={handleDisconnect}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="disconnect-calendar-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Disconnect
                </Button>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="mb-4">
              <h2 className="text-lg font-bold text-black">Choose where new bookings appear</h2>
              <p className="text-sm text-gray-600 mt-1">Confirmed-quote bookings will be written to this calendar.</p>
            </div>

            {loadingCalendars ? (
              <p className="text-sm text-gray-600">Loading calendars…</p>
            ) : calendars.length === 0 ? (
              <p className="text-sm text-gray-600">No writable calendars found on this account.</p>
            ) : (
              <div className="space-y-2" data-testid="calendar-picker-list">
                {calendars.map((cal) => {
                  const selected = status.selectedCalendarId
                    ? status.selectedCalendarId === cal.id
                    : cal.primary;
                  return (
                    <button
                      key={cal.id}
                      type="button"
                      onClick={() => handleSelect(cal)}
                      disabled={saving}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-colors flex items-center justify-between ${
                        selected ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-400'
                      }`}
                      data-testid={`calendar-option-${cal.id}`}
                    >
                      <div>
                        <p className="font-medium text-black">{cal.summary}</p>
                        <p className="text-xs text-gray-500">
                          {cal.primary ? 'Primary calendar' : cal.accessRole}
                        </p>
                      </div>
                      {selected && (
                        <span className="text-xs font-semibold text-white bg-black rounded-full px-3 py-1">
                          Selected
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default CalendarSettings;
